var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import jwt_decode from 'jwt-decode';
/**
 * An authProvider which handles authentication via the Keycloak server.
 *
 * @example
 * ```tsx
 * import React, { useState, useRef, useEffect } from 'react';
 * import { Admin, Resource, AuthProvider, DataProvider } from 'react-admin';
 * import simpleRestProvider from 'ra-data-simple-rest';
 * import Keycloak, {
 *     KeycloakConfig,
 *     KeycloakTokenParsed,
 *     KeycloakInitOptions,
 * } from 'keycloak-js';
 * import { keycloakAuthProvider, httpClient } from 'ra-keycloak';
 *
 * import comments from './comments';
 * import i18nProvider from './i18nProvider';
 * import Layout from './Layout';
 * import posts from './posts';
 * import users from './users';
 * import tags from './tags';
 *
 * const config: KeycloakConfig = {
 *     url: '$KEYCLOAK_URL',
 *     realm: '$KEYCLOAK_REALM',
 *     clientId: '$KEYCLOAK_CLIENT_ID',
 * };
 *
 * // here you can set options for the keycloak client
 * const initOptions: KeycloakInitOptions = { onLoad: 'login-required' };
 *
 * // here you can implement the permission mapping logic for react-admin
 * const getPermissions = (decoded: KeycloakTokenParsed) => {
 *     const roles = decoded?.realm_access?.roles;
 *     if (!roles) {
 *         return false;
 *     }
 *     if (roles.includes('admin')) return 'admin';
 *     if (roles.includes('user')) return 'user';
 *     return false;
 * };
 *
 * const raKeycloakOptions = {
 *     onPermissions: getPermissions,
 * };
 *
 * const App = () => {
 *     const [keycloak, setKeycloak] = useState<Keycloak>(undefined);
 *     const authProvider = useRef<AuthProvider>(undefined);
 *     const dataProvider = useRef<DataProvider>(undefined);
 *
 *     useEffect(() => {
 *         const initKeyCloakClient = async () => {
 *             // init the keycloak client
 *             const keycloakClient = new Keycloak(config);
 *             await keycloakClient.init(initOptions);
 *             // use keycloakAuthProvider to create an authProvider
 *             authProvider.current = keycloakAuthProvider(
 *                 keycloakClient,
 *                 raKeycloakOptions
 *             );
 *             // example dataProvider using the httpClient helper
 *             dataProvider.current = simpleRestProvider(
 *                 '$API_URL',
 *                 httpClient(keycloakClient)
 *             );
 *             setKeycloak(keycloakClient);
 *         };
 *         if (!keycloak) {
 *             initKeyCloakClient();
 *         }
 *     }, [keycloak]);
 *
 *     // hide the admin until the keycloak client is ready
 *     if (!keycloak) return <p>Loading...</p>;
 *
 *     return (
 *         <Admin
 *             authProvider={authProvider.current}
 *             dataProvider={dataProvider.current}
 *             i18nProvider={i18nProvider}
 *             title="Example Admin"
 *             layout={Layout}
 *         >
 *             {permissions => (
 *                 <>
 *                     <Resource name="posts" {...posts} />
 *                     <Resource name="comments" {...comments} />
 *                     <Resource name="tags" {...tags} />
 *                     {permissions === 'admin' ? (
 *                         <Resource name="users" {...users} />
 *                     ) : null}
 *                 </>
 *             )}
 *         </Admin>
 *     );
 * };
 * ```
 *
 * @param client the keycloak client
 * @param options.onPermissions function used to transform the permissions fetched from Keycloak into a permissions object in the form of what your react-admin app expects
 * @param options.loginRedirectUri URI used to override the redirect URI after successful login
 * @param options.logoutRedirectUri URI used to override the redirect URI after successful logout
 *
 * @returns an authProvider ready to be used by React-Admin.
 */
export var keycloakAuthProvider = function (client, options) {
    if (options === void 0) { options = {}; }
    return ({
        login: function () {
            var _a;
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_b) {
                    return [2 /*return*/, client.login({
                            redirectUri: (_a = options.loginRedirectUri) !== null && _a !== void 0 ? _a : window.location.origin,
                        })];
                });
            });
        },
        logout: function () {
            var _a;
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_b) {
                    return [2 /*return*/, client.logout({
                            redirectUri: (_a = options.logoutRedirectUri) !== null && _a !== void 0 ? _a : window.location.origin,
                        })];
                });
            });
        },
        checkError: function () {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    return [2 /*return*/, Promise.resolve()];
                });
            });
        },
        checkAuth: function () {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    return [2 /*return*/, client.authenticated && client.token
                            ? Promise.resolve()
                            : Promise.reject('Failed to obtain access token.')];
                });
            });
        },
        getPermissions: function () {
            return __awaiter(this, void 0, void 0, function () {
                var decoded;
                return __generator(this, function (_a) {
                    if (!client.token) {
                        return [2 /*return*/, Promise.resolve(false)];
                    }
                    decoded = jwt_decode(client.token);
                    return [2 /*return*/, Promise.resolve(options.onPermissions ? options.onPermissions(decoded) : decoded)];
                });
            });
        },
        getIdentity: function () {
            return __awaiter(this, void 0, void 0, function () {
                var decoded, id, fullName;
                return __generator(this, function (_a) {
                    if (client.token) {
                        decoded = jwt_decode(client.token);
                        id = decoded.sub || '';
                        fullName = decoded.preferred_username;
                        return [2 /*return*/, Promise.resolve({ id: id, fullName: fullName })];
                    }
                    return [2 /*return*/, Promise.reject('Failed to get identity.')];
                });
            });
        },
    });
};
